/**
 * PRIVA Governance — SAP Connections.
 *
 * Create / edit / set-default / delete reusable SAP connections. Secrets are
 * encrypted at rest and NEVER returned by the API — the form shows a
 * "Configured" badge and keeps secret fields blank on edit (blank = keep).
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  Loader2, RefreshCw, ShieldCheck, Plus, Pencil, Trash2, Star, AlertTriangle,
} from "lucide-react";
import {
  listConnections, createConnection, updateConnection,
  deleteConnection, setDefaultConnection, extractError,
} from "../lib/privaAdminApi";
import { listSmartewmClients } from "../lib/adminApi";
import EnvBadge from "../components/EnvBadge";

const AUTH_TYPES = ["Basic", "OAuth", "SAML", "APIKey", "DestinationService", "None"];
const ENVIRONMENTS = ["DEV", "QA", "PROD", "TEST", "SANDBOX"];
const ALL_CLIENTS = "__all__";
const DEMO_CLIENT = { clientId: "demo-client", clientName: "Demo Client (Mock)" };

const emptyForm = {
  connectionId: null,
  clientId: "demo-client",
  name: "",
  environment: "DEV",
  baseUrl: "",
  authType: "Basic",
  username: "",
  password: "",
  verifySsl: false,
  timeoutSeconds: 10,
  active: true,
  isDefault: false,
};

export default function SapConnectionsPage() {
  const [clientFilter, setClientFilter] = useState("");
  const [clients, setClients] = useState([]);
  const [rows, setRows] = useState([]);
  const [encryptionOk, setEncryptionOk] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    listSmartewmClients()
      .then((d) => setClients([...(d.clients || []), DEMO_CLIENT]))
      .catch(() => setClients([DEMO_CLIENT]));
  }, []);

  const clientName = (cid) => clients.find((c) => c.clientId === cid)?.clientName || cid;

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    listConnections(clientFilter.trim())
      .then((data) => {
        setRows(data.connections || []);
        setEncryptionOk(data.encryptionConfigured !== false);
      })
      .catch((e) => setError(extractError(e, "Failed to load connections.")))
      .finally(() => setLoading(false));
  }, [clientFilter]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setForm({ ...emptyForm, clientId: clientFilter || clients[0]?.clientId || "demo-client" });
    setEditing(false);
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setForm({
      connectionId: row.connectionId,
      clientId: row.clientId,
      name: row.connectionName || row.name || "",
      environment: row.environment || "DEV",
      baseUrl: row.baseUrl || "",
      authType: row.authType || "Basic",
      username: "", // secret — blank = keep existing
      password: "", // secret — blank = keep existing
      verifySsl: !!row.verifySsl,
      timeoutSeconds: row.timeoutSeconds ?? 10,
      active: row.active !== false,
      isDefault: !!row.isDefault,
      _usernameConfigured: row.usernameConfigured,
      _passwordConfigured: row.passwordConfigured,
    });
    setEditing(true);
    setDialogOpen(true);
  };

  const setField = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim() || !form.baseUrl.trim() || !form.clientId.trim()) {
      toast.error("Name, Business Client and Base URL are required.");
      return;
    }
    setSaving(true);
    const payload = {
      clientId: form.clientId.trim(),
      name: form.name.trim(),
      environment: form.environment,
      baseUrl: form.baseUrl.trim(),
      authType: form.authType,
      verifySsl: form.verifySsl,
      timeoutSeconds: Number(form.timeoutSeconds) || 10,
      active: form.active,
      isDefault: form.isDefault,
    };
    // Only send secrets when the admin actually typed them (blank = keep).
    if (form.username.trim()) payload.username = form.username;
    if (form.password.trim()) payload.password = form.password;
    try {
      if (editing) {
        await updateConnection(form.connectionId, payload);
        toast.success("Connection updated.");
      } else {
        await createConnection(payload);
        toast.success("Connection created.");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(extractError(e, "Save failed."));
    } finally {
      setSaving(false);
    }
  };

  const doSetDefault = async (row) => {
    try {
      await setDefaultConnection(row.connectionId);
      toast.success(`"${row.connectionName}" is now the default.`);
      load();
    } catch (e) {
      toast.error(extractError(e));
    }
  };

  const doDelete = async (row) => {
    if (!window.confirm(`Delete connection "${row.connectionName}"? This cannot be undone.`)) return;
    try {
      await deleteConnection(row.connectionId);
      toast.success("Connection deleted.");
      load();
    } catch (e) {
      toast.error(extractError(e));
    }
  };

  return (
    <div className="p-6 max-w-[1480px] mx-auto" data-testid="sap-connections-page">
      <header className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1.5">
            <ShieldCheck className="w-4 h-4 text-[#025979]" />
            <p className="text-[10px] uppercase tracking-widest text-[#025979] font-semibold">PRIVA Governance</p>
          </div>
          <h1 className="text-xl font-bold text-[#111827]" style={{ fontFamily: "Chivo, sans-serif" }}>SAP Connections</h1>
          <p className="text-xs text-[#6B7280] mt-1 max-w-2xl">
            Reusable SAP system connections. One connection can serve every signal
            service. Credentials are encrypted at rest and never displayed again.
          </p>
        </div>
        <div className="flex items-end gap-3">
          <label className="flex flex-col gap-1 text-[10px] uppercase tracking-wider text-[#6B7280]">
            Business Client
            <Select value={clientFilter || ALL_CLIENTS} onValueChange={(v) => setClientFilter(v === ALL_CLIENTS ? "" : v)}>
              <SelectTrigger className="h-8 text-xs w-48" data-testid="sap-conn-client-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_CLIENTS}>All Clients</SelectItem>
                {clients.map((c) => <SelectItem key={c.clientId} value={c.clientId}>{c.clientName}</SelectItem>)}
              </SelectContent>
            </Select>
          </label>
          <button type="button" onClick={load} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[#025979] border border-[#025979] rounded-sm hover:bg-[#025979]/5" data-testid="sap-conn-refresh">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
          <Button onClick={openCreate} className="h-8 bg-[#025979] hover:bg-[#03738C] text-xs" data-testid="sap-conn-create-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> New Connection
          </Button>
        </div>
      </header>

      {!encryptionOk && (
        <div className="mb-4 flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-sm text-xs text-amber-800" data-testid="sap-conn-encryption-warning">
          <AlertTriangle className="w-4 h-4" />
          Secret encryption key is not configured — saving SAP credentials is blocked. Set SAP_SECRET_ENCRYPTION_KEY in the backend.
        </div>
      )}
      {error && <div className="text-red-600 text-xs py-2" data-testid="sap-conn-error">{error}</div>}

      <div className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm overflow-x-auto">
        <table className="min-w-full text-xs" data-testid="sap-conn-table">
          <thead className="bg-[#F8FAFC] text-[10px] uppercase tracking-wider text-[#6B7280]">
            <tr>
              <Th>Name</Th><Th>Business Client</Th><Th>Env</Th><Th>Base URL</Th><Th>Auth</Th>
              <Th>Verify SSL</Th><Th>Timeout</Th><Th>Active</Th><Th>Default</Th><Th>Secret</Th><Th>Actions</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E5E7EB]">
            {loading ? (
              <tr><td colSpan={11} className="text-center py-12 text-[#6B7280]"><Loader2 className="w-4 h-4 mx-auto animate-spin" /></td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={11} className="text-center py-10 text-[#6B7280]" data-testid="sap-conn-empty">No SAP connections yet. Create one to get started.</td></tr>
            ) : (
              rows.map((r) => (
                <tr key={r.connectionId} className="hover:bg-[#F8FAFC]/70" data-testid={`sap-conn-row-${r.connectionId}`}>
                  <Td className="font-medium text-[#111827]">{r.connectionName}</Td>
                  <Td className="text-[#374151]">{clientName(r.clientId)}</Td>
                  <Td><EnvBadge env={r.environment} /></Td>
                  <Td className="font-mono text-[11px] max-w-[220px] truncate">{r.baseUrl}</Td>
                  <Td>{r.authType}</Td>
                  <Td>{r.verifySsl ? "Yes" : "No"}</Td>
                  <Td>{r.timeoutSeconds}s</Td>
                  <Td>{r.active ? <span className="text-emerald-600">Active</span> : <span className="text-[#9CA3AF]">Disabled</span>}</Td>
                  <Td>{r.isDefault ? <Badge className="bg-[#E0F2FE] text-[#025979] border border-[#025979]/20">Default</Badge> : <span className="text-[#9CA3AF]">—</span>}</Td>
                  <Td>{r.credentialsConfigured ? <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200">Configured</Badge> : <span className="text-[#9CA3AF]">None</span>}</Td>
                  <Td>
                    <div className="flex items-center gap-1.5">
                      <IconBtn title="Edit" onClick={() => openEdit(r)} testId={`sap-conn-edit-${r.connectionId}`}><Pencil className="w-3.5 h-3.5" /></IconBtn>
                      {!r.isDefault && <IconBtn title="Set default" onClick={() => doSetDefault(r)} testId={`sap-conn-default-${r.connectionId}`}><Star className="w-3.5 h-3.5" /></IconBtn>}
                      <IconBtn title="Delete" onClick={() => doDelete(r)} testId={`sap-conn-delete-${r.connectionId}`} danger><Trash2 className="w-3.5 h-3.5" /></IconBtn>
                    </div>
                  </Td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="sap-conn-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit SAP Connection" : "New SAP Connection"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <Field label="Name *"><Input value={form.name} onChange={(e) => setField("name")(e.target.value)} className="h-8 text-xs" data-testid="sap-conn-form-name" /></Field>
            <Field label="Business Client *">
              <Select value={form.clientId} onValueChange={setField("clientId")}>
                <SelectTrigger className="h-8 text-xs" data-testid="sap-conn-form-clientid"><SelectValue placeholder="Select client" /></SelectTrigger>
                <SelectContent>{clients.map((c) => <SelectItem key={c.clientId} value={c.clientId}>{c.clientName}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Environment">
              <Select value={form.environment} onValueChange={setField("environment")}>
                <SelectTrigger className="h-8 text-xs" data-testid="sap-conn-form-env"><SelectValue /></SelectTrigger>
                <SelectContent>{ENVIRONMENTS.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Auth Type">
              <Select value={form.authType} onValueChange={setField("authType")}>
                <SelectTrigger className="h-8 text-xs" data-testid="sap-conn-form-auth"><SelectValue /></SelectTrigger>
                <SelectContent>{AUTH_TYPES.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Base URL *" full><Input value={form.baseUrl} onChange={(e) => setField("baseUrl")(e.target.value)} placeholder="https://host:50000" className="h-8 text-xs" data-testid="sap-conn-form-baseurl" /></Field>
            <Field label={`Username ${editing && form._usernameConfigured ? "(configured)" : ""}`}>
              <Input value={form.username} onChange={(e) => setField("username")(e.target.value)} placeholder={editing && form._usernameConfigured ? "•••• leave blank to keep" : ""} className="h-8 text-xs" data-testid="sap-conn-form-username" autoComplete="off" />
            </Field>
            <Field label={`Password ${editing && form._passwordConfigured ? "(configured)" : ""}`}>
              <Input type="password" value={form.password} onChange={(e) => setField("password")(e.target.value)} placeholder={editing && form._passwordConfigured ? "•••• leave blank to keep" : ""} className="h-8 text-xs" data-testid="sap-conn-form-password" autoComplete="new-password" />
            </Field>
            <Field label="Timeout (s)"><Input type="number" value={form.timeoutSeconds} onChange={(e) => setField("timeoutSeconds")(e.target.value)} className="h-8 text-xs" data-testid="sap-conn-form-timeout" /></Field>
            <ToggleField label="Verify SSL" checked={form.verifySsl} onChange={setField("verifySsl")} testId="sap-conn-form-verifyssl" />
            <ToggleField label="Active" checked={form.active} onChange={setField("active")} testId="sap-conn-form-active" />
            <ToggleField label="Default" checked={form.isDefault} onChange={setField("isDefault")} testId="sap-conn-form-default" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="h-8 text-xs" data-testid="sap-conn-form-cancel">Cancel</Button>
            <Button onClick={save} disabled={saving} className="h-8 bg-[#025979] hover:bg-[#03738C] text-xs" data-testid="sap-conn-form-save">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (editing ? "Save Changes" : "Create")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Th({ children }) { return <th className="text-left font-medium px-3 py-2 whitespace-nowrap">{children}</th>; }
function Td({ children, className = "" }) { return <td className={`px-3 py-2 align-top ${className}`}>{children}</td>; }
function Field({ label, children, full }) {
  return <label className={`flex flex-col gap-1 text-[10px] uppercase tracking-wider text-[#6B7280] ${full ? "col-span-2" : ""}`}>{label}{children}</label>;
}
function ToggleField({ label, checked, onChange, testId }) {
  return (
    <div className="flex items-center justify-between gap-2 pt-4">
      <span className="text-[10px] uppercase tracking-wider text-[#6B7280]">{label}</span>
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testId} />
    </div>
  );
}
function IconBtn({ children, onClick, title, testId, danger }) {
  return (
    <button type="button" title={title} onClick={onClick} data-testid={testId}
      className={`p-1.5 rounded-sm border transition-colors ${danger ? "border-red-200 text-red-600 hover:bg-red-50" : "border-[#E5E7EB] text-[#374151] hover:bg-[#F8FAFC]"}`}>
      {children}
    </button>
  );
}
